import { useState } from "preact/hooks";
import { ExclamationTriangleIcon, TrashIcon } from "@heroicons/react/24/outline";
import { api } from "../../lib/api-client";
import { adminHref } from "../../lib/platform-admin";
import { Alert, Button, Card, CardBody, Input } from "../ui";
import type { ProjectDto } from "@shared/types";

type Props = {
  project: ProjectDto;
};

export function DangerZoneSection({ project }: Props) {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onDelete(e: Event) {
    e.preventDefault();
    if (confirm !== project.slug) return;
    setDeleting(true);
    setError(null);
    try {
      await api.deleteProject(project.id, confirm);
      location.href = adminHref("/");
    } catch (err) {
      setError((err as Error).message);
      setDeleting(false);
    }
  }

  return (
    <div class="space-y-4">
      <h3 class="flex items-center gap-1.5 text-base font-semibold tracking-tight text-danger-fg">
        <ExclamationTriangleIcon class="h-5 w-5" />
        危険な操作
      </h3>

      <Card tone="danger">
        <CardBody>
          <div class="flex flex-wrap items-center justify-between gap-4">
            <div class="space-y-1">
              <div class="text-sm font-medium text-fg">
                プロジェクトを削除
              </div>
              <p class="text-xs text-fg-subtle">
                コンテンツ・画像・APIキー・R2
                のオブジェクトをすべて削除します。元に戻せません。
              </p>
            </div>
            {!open && (
              <Button
                variant="danger"
                iconLeft={<TrashIcon class="h-4 w-4" />}
                onClick={() => setOpen(true)}
              >
                削除…
              </Button>
            )}
          </div>

          {open && (
            <form
              onSubmit={onDelete}
              class="mt-4 space-y-3 border-t border-border-subtle pt-4"
            >
              <p class="text-xs text-fg-muted">
                確認のため、プロジェクトの slug{" "}
                <code class="rounded bg-bg-inset px-1.5 py-0.5 font-mono text-fg">
                  {project.slug}
                </code>{" "}
                を入力してください。
              </p>
              <Input
                value={confirm}
                onInput={(e) =>
                  setConfirm((e.target as HTMLInputElement).value)
                }
                placeholder={project.slug}
                autoComplete="off"
                spellcheck={false}
                monospace
                invalid={
                  confirm.length > 0 && confirm !== project.slug
                }
              />
              <div class="flex flex-wrap items-center gap-2">
                <Button
                  type="submit"
                  variant="dangerSolid"
                  iconLeft={<TrashIcon class="h-4 w-4" />}
                  loading={deleting}
                  disabled={deleting || confirm !== project.slug}
                >
                  {deleting ? "削除中…" : "完全に削除する"}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setOpen(false);
                    setConfirm("");
                    setError(null);
                  }}
                  disabled={deleting}
                >
                  キャンセル
                </Button>
              </div>
              {error && (
                <Alert tone="danger">
                  {error}
                </Alert>
              )}
            </form>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
